/**
 * 师资详情页面
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Descriptions,
  Button,
  Space,
  message,
  Spin,
  Tag,
  Image,
  Typography,
  Modal
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  UserOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import facultyService, { Faculty } from '../services/facultyService';

const { Title, Text } = Typography;

const FacultyDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [faculty, setFaculty] = useState<Faculty | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id) {
      loadFacultyDetail();
    }
  }, [id]);

  /**
   * 加载师资详情
   */
  const loadFacultyDetail = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const data = await facultyService.getFacultyById(parseInt(id));
      setFaculty(data);
    } catch (error: any) {
      message.error(error.message || '加载师资详情失败');
      navigate('/faculty');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 返回列表
   */
  const handleBack = () => {
    navigate('/faculty');
  };

  /**
   * 编辑师资
   */
  const handleEdit = () => {
    navigate(`/faculty/edit/${id}`);
  };

  /**
   * 删除师资
   */
  const handleDelete = () => {
    if (!faculty) return;

    Modal.confirm({
      title: '确认删除',
      content: `确定要删除师资"${faculty.name}"吗？此操作不可恢复。`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await facultyService.deleteFaculty(faculty.id);
          message.success('删除成功');
          navigate('/faculty');
        } catch (error: any) {
          message.error(error.message || '删除失败');
        }
      }
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!faculty) {
    return null;
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        {/* 头部操作区 */}
        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col flex="auto">
              <Space>
                <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
                  返回
                </Button>
                <Title level={3} style={{ margin: 0 }}>
                  师资详情
                </Title>
              </Space>
            </Col>
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                >
                  编辑
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                >
                  删除
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <Row gutter={24}>
          {/* 基本信息卡片 */}
          <Col span={24}>
            <Card
              title={
                <Space>
                  <UserOutlined />
                  <span>基本信息</span>
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              <Descriptions column={3} bordered>
                <Descriptions.Item label="姓名" span={1}>
                  <Text strong style={{ fontSize: 16 }}>{faculty.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="性别" span={1}>
                  <Tag color={faculty.gender === '男' ? 'blue' : 'pink'}>
                    {faculty.gender}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="师资类别" span={1}>
                  <Tag color="blue">{faculty.category}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="地区" span={1}>
                  <Space>
                    <EnvironmentOutlined />
                    {faculty.region}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="常驻地" span={1}>
                  {faculty.residence || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="联系电话" span={1}>
                  {faculty.phone ? (
                    <Space>
                      <PhoneOutlined />
                      <Text copyable>{faculty.phone}</Text>
                    </Space>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="简介" span={3}>
                  {faculty.introduction || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* 证书信息卡片 */}
          <Col span={24}>
            <Card
              title={
                <Space>
                  <SafetyCertificateOutlined />
                  <span>证书信息</span>
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              <Descriptions column={3} bordered>
                <Descriptions.Item label="证书类别" span={1}>
                  {faculty.certificate_category ? (
                    <Tag color="green">{faculty.certificate_category}</Tag>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="证书级别" span={1}>
                  {faculty.certificate_level || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="证书名称" span={1}>
                  {faculty.certificate_name || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="教师资格证" span={3}>
                  {faculty.teacher_certificate || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* 费用和评价卡片 */}
          <Col span={24}>
            <Card
              title="费用和评价"
              style={{ marginBottom: 16 }}
            >
              <Descriptions column={2} bordered>
                <Descriptions.Item label="课酬(元/天)" span={1}>
                  {faculty.daily_fee ? (
                    <Text strong style={{ color: '#52c41a', fontSize: 16 }}>
                      ¥{faculty.daily_fee.toLocaleString()}
                    </Text>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="当地评价" span={1}>
                  {faculty.local_evaluation ? (
                    <Tag color={
                      faculty.local_evaluation.includes('优秀') ? 'green' :
                      faculty.local_evaluation.includes('良好') ? 'blue' : 'default'
                    }>
                      {faculty.local_evaluation}
                    </Tag>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="备注" span={2}>
                  {faculty.remarks || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* 证件文件卡片 */}
          <Col span={24}>
            <Card
              title="证件文件"
              style={{ marginBottom: 16 }}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Card size="small" title="身份证扫描件">
                    {faculty.id_card_file_path ? (
                      <Image
                        width="100%"
                        src={faculty.id_card_file_path}
                        placeholder={
                          <div style={{ textAlign: 'center', padding: 20 }}>
                            加载中...
                          </div>
                        }
                      />
                    ) : (
                      <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                        暂未上传
                      </div>
                    )}
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small" title="职称证书扫描件">
                    {faculty.title_cert_file_path ? (
                      <Image
                        width="100%"
                        src={faculty.title_cert_file_path}
                        placeholder={
                          <div style={{ textAlign: 'center', padding: 20 }}>
                            加载中...
                          </div>
                        }
                      />
                    ) : (
                      <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                        暂未上传
                      </div>
                    )}
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small" title="相关资格证书扫描件">
                    {faculty.qualification_cert_file_path ? (
                      <Image
                        width="100%"
                        src={faculty.qualification_cert_file_path}
                        placeholder={
                          <div style={{ textAlign: 'center', padding: 20 }}>
                            加载中...
                          </div>
                        }
                      />
                    ) : (
                      <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                        暂未上传
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* 系统信息卡片 */}
          <Col span={24}>
            <Card
              title="系统信息"
              size="small"
            >
              <Descriptions column={2} size="small">
                <Descriptions.Item label="创建时间">
                  {new Date(faculty.created_at).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="更新时间">
                  {new Date(faculty.updated_at).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="师资ID">
                  {faculty.id}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default FacultyDetail;